import { create } from 'zustand'
import type { Product, WeightUnit } from '@/lib/types'

export interface CartLine {
  productId: number
  name: string
  sku: string
  unitPriceUsd: number
  listPriceUsd: number
  vatRate: number
  /** In {@link quantityUnit} when that is set, otherwise in the product's own stock unit. */
  quantity: number
  discountUsd: number
  isWeighed: boolean
  /**
   * The weight unit the cashier worked in, when it differs from how the item is stocked.
   *
   * Sent to the server as-is; the conversion to the stock unit happens there, so the price and the
   * stock movement are both decided by the same tested arithmetic rather than by this browser.
   */
  quantityUnit?: WeightUnit
  /** The product's own unit, for labelling the line. */
  unit: string
  /**
   * The same weight expressed in the product's stock unit, as the server worked it out.
   *
   * Screen totals only. Price is quoted per stock unit, so multiplying it by 300 grams would show
   * a cart total of 1,200 dollars for a dollar-twenty of charcoal. Taken from the server's own
   * quote rather than converted here, so the running total agrees with what checkout will charge.
   */
  stockQuantity?: number

  /**
   * The most of this the cart may hold, or undefined when there is no ceiling.
   *
   * Set from the product's own quantity on hand for anything that may not go negative. The server
   * refuses an oversell either way — {@link InsufficientStockException} — but a refusal arrives
   * only once the whole sale is submitted, with a queue waiting and a cart to unpick. Stopping at
   * the last one on the shelf is the difference between a cashier knowing immediately and finding
   * out at the end.
   *
   * Left undefined for a weighed line entered in another unit: the ceiling is in stock units and
   * the line's quantity is not, so comparing them would clamp grams against kilograms.
   */
  maxQuantity?: number

  /**
   * Set when this line is a one-off priced at the counter rather than a catalogue item.
   *
   * The line still carries a {@link productId} — a negative synthetic one — purely so everything
   * keyed on it keeps working: quantity, discount, removal. The server is sent these details
   * instead and writes the line against its own shared placeholder product.
   */
  custom?: { name: string; priceUsd: number; costUsd: number }
}

/**
 * The account the sale is charged to, held whole rather than as a bare id so the payment sheet
 * can show the remaining credit without a second round trip mid-checkout.
 *
 * It is a snapshot: the server re-reads the real balance and enforces the limit at checkout, so a
 * stale figure here can mislead a cashier for a moment but can never let a charge through.
 */
export interface AttachedCustomer {
  id: number
  name: string
  balanceUsd: number
  creditLimitUsd: number
  availableCreditUsd: number
}

interface CartState {
  lines: CartLine[]
  saleDiscountUsd: number
  customer: AttachedCustomer | null
  note: string

  add: (
    product: Product,
    quantity?: number,
    quantityUnit?: WeightUnit,
    stockQuantity?: number,
  ) => void
  setQuantity: (productId: number, quantity: number) => void
  addCustom: (name: string, priceUsd: number, costUsd: number, quantity: number) => void
  setUnitPrice: (productId: number, price: number) => void
  setLineDiscount: (productId: number, discount: number) => void
  remove: (productId: number) => void
  setSaleDiscount: (amount: number) => void
  setCustomer: (customer: AttachedCustomer | null) => void
  setNote: (note: string) => void
  clear: () => void
}

/**
 * The line's quantity, held to its ceiling where it has one.
 *
 * Rounded down rather than refused: a cashier pressing "+" past the last one means they want as
 * many as there are, and a button that silently does nothing reads as a broken till.
 */
function capped(quantity: number, maxQuantity: number | undefined): number {
  return maxQuantity === undefined ? quantity : Math.min(quantity, maxQuantity)
}

export const useCart = create<CartState>((set) => ({
  lines: [],
  saleDiscountUsd: 0,
  customer: null,
  note: '',

  add: (product, quantity = 1, quantityUnit, stockQuantity) =>
    set((state) => {
      const existing = state.lines.find((line) => line.productId === product.id)

      // No ceiling for an item the shop has said may go negative, and none for a weight entered
      // in a unit other than the one stock is held in — see CartLine.maxQuantity.
      const maxQuantity =
        product.allowNegativeStock || quantityUnit !== undefined
          ? undefined
          : Math.max(product.quantityOnHand, 0)

      // Scanning the same item twice adds to the line rather than stacking duplicates —
      // a receipt reading "Water x3" is what a customer expects, not three separate rows.
      // Two weighings only merge when they were entered in the same unit; 300 g and 0.5 kg
      // cannot be added together here, so the second becomes its own line and the server
      // converts each on its own terms.
      if (existing && existing.quantityUnit === quantityUnit) {
        return {
          lines: state.lines.map((line) =>
            line.productId === product.id
              ? {
                  ...line,
                  quantity: capped(line.quantity + quantity, maxQuantity),
                  stockQuantity:
                    line.stockQuantity === undefined || stockQuantity === undefined
                      ? undefined
                      : line.stockQuantity + stockQuantity,
                }
              : line,
          ),
        }
      }

      // Same item, different unit: the previous weighing is replaced rather than added to, since
      // 300 g and 0.5 kg cannot be summed without doing the conversion here.
      if (existing) {
        return {
          lines: state.lines.map((line) =>
            line.productId === product.id
              ? { ...line, quantity: capped(quantity, maxQuantity), quantityUnit, stockQuantity }
              : line,
          ),
        }
      }

      return {
        lines: [
          ...state.lines,
          {
            productId: product.id,
            name: product.name,
            sku: product.sku,
            unitPriceUsd: product.priceUsd,
            listPriceUsd: product.priceUsd,
            vatRate: product.vatRate,
            quantity: capped(quantity, maxQuantity),
            discountUsd: 0,
            isWeighed: product.isWeighed,
            quantityUnit,
            unit: product.unit,
            stockQuantity,
            maxQuantity,
          },
        ],
      }
    }),

  setQuantity: (productId, quantity) =>
    set((state) => ({
      // Dropping to zero removes the line, which is what pressing backspace to nothing means.
      lines:
        quantity <= 0
          ? state.lines.filter((line) => line.productId !== productId)
          : state.lines.map((line) =>
              line.productId === productId
                ? { ...line, quantity: capped(quantity, line.maxQuantity) }
                : line,
            ),
    })),


  /**
   * Puts a one-off on the cart: something not in the catalogue, named and priced at the counter.
   *
   * Each one is its own line rather than merging with a previous custom item of the same name,
   * because two things a cashier typed separately are two things — the second is not another of
   * the first, and merging them would silently double a quantity.
   */
  addCustom: (name, priceUsd, costUsd, quantity) =>
    set((state) => ({
      lines: [
        ...state.lines,
        {
          // Negative and unique, so nothing collides with a real product id.
          productId: -Date.now() - state.lines.length,
          name: name.trim(),
          sku: '',
          unitPriceUsd: priceUsd,
          listPriceUsd: priceUsd,
          vatRate: 0,
          quantity,
          discountUsd: 0,
          isWeighed: false,
          unit: 'Each',
          custom: { name: name.trim(), priceUsd, costUsd },
        },
      ],
    })),

  setUnitPrice: (productId, price) =>
    set((state) => ({
      lines: state.lines.map((line) =>
        line.productId === productId ? { ...line, unitPriceUsd: Math.max(0, price) } : line,
      ),
    })),

  setLineDiscount: (productId, discount) =>
    set((state) => ({
      lines: state.lines.map((line) =>
        line.productId === productId ? { ...line, discountUsd: Math.max(0, discount) } : line,
      ),
    })),

  remove: (productId) =>
    set((state) => ({ lines: state.lines.filter((line) => line.productId !== productId) })),

  setSaleDiscount: (amount) => set({ saleDiscountUsd: Math.max(0, amount) }),
  setCustomer: (customer) => set({ customer }),
  setNote: (note) => set({ note }),

  // The customer is cleared with everything else: the next person in the queue must never
  // inherit the last one's account.
  clear: () => set({ lines: [], saleDiscountUsd: 0, customer: null, note: '' }),
}))

/**
 * Indicative totals for the screen only.
 *
 * These are JavaScript numbers and therefore not to be trusted with money — the server
 * recomputes everything in decimal and its answer is the one that gets charged. This exists so
 * the cashier sees a running total without a round trip per scan.
 */
export function cartPreview(
  lines: CartLine[],
  saleDiscountUsd: number,
  vatInclusive: boolean,
  vatEnabled = true,
) {
  const gross = lines.reduce((sum, line) => sum + lineGross(line), 0)
  const afterDiscount = Math.max(0, gross - saleDiscountUsd)

  const vat = !vatEnabled
    ? 0
    : lines.reduce((sum, line) => {
        const thisLine = lineGross(line)
        const share = gross > 0 ? thisLine / gross : 0
        const charged = thisLine - saleDiscountUsd * share
        return sum + (vatInclusive ? charged - charged / (1 + line.vatRate) : charged * line.vatRate)
      }, 0)

  return {
    // A weighed line counts as one item, not as its weight: "300 items" for 300 grams of
    // charcoal would be nonsense on the cart bar.
    itemCount: lines.reduce((sum, line) => sum + (line.quantityUnit ? 1 : line.quantity), 0),
    gross,
    discount: saleDiscountUsd,
    vat,
    total: vatInclusive ? afterDiscount : afterDiscount + vat,
  }
}

/**
 * What one line comes to on screen.
 *
 * Priced against the stock-unit quantity, because the price is quoted per stock unit — using the
 * weight as typed would price 300 grams as though it were 300 kilos.
 */
export function lineGross(line: CartLine): number {
  return line.unitPriceUsd * (line.stockQuantity ?? line.quantity) - line.discountUsd
}
